import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { IconSymbol } from "@/components/ui/IconSymbol";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  AccessibilityInfo,
  ActivityIndicator,
  Animated,
  Pressable,
  StyleSheet,
  View,
} from "react-native";

type ToastType = "success" | "error" | "info" | "loading";

type ToastMessage = {
  id: number;
  messages: string[];
  type: ToastType;
};

type AddToast = {
  (messages: string[], type: "loading"): number;
  (message: string, type: "success" | "error" | "info"): number;
};

type ToastContextType = {
  addToast: AddToast;
  removeToast: (id: number) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

type ToastProviderProps = {
  children: ReactNode;
};

const LINE_HEIGHT = 24;
const SLIDE_DURATION = 300;
const DEFAULT_CYCLE_MS = 3500;

const TelepromterText = ({
  messages,
  intervalMs = DEFAULT_CYCLE_MS,
}: {
  messages: string[];
  intervalMs?: number;
}) => {
  const [idx, setIdx] = useState(0);
  const translateY = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(1)).current;
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled().then((value) => {
      if (mounted) reducedMotionRef.current = value;
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (messages.length <= 1) return;

    const timer = setInterval(() => {
      if (reducedMotionRef.current) {
        setIdx((i) => (i + 1) % messages.length);
        return;
      }

      Animated.parallel([
        Animated.timing(translateY, {
          toValue: -LINE_HEIGHT,
          duration: SLIDE_DURATION,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0,
          duration: SLIDE_DURATION,
          useNativeDriver: true,
        }),
      ]).start(({ finished }) => {
        if (!finished) return;
        setIdx((i) => (i + 1) % messages.length);
        translateY.setValue(LINE_HEIGHT);
        Animated.parallel([
          Animated.timing(translateY, {
            toValue: 0,
            duration: SLIDE_DURATION,
            useNativeDriver: true,
          }),
          Animated.timing(opacity, {
            toValue: 1,
            duration: SLIDE_DURATION,
            useNativeDriver: true,
          }),
        ]).start();
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [messages, intervalMs, translateY, opacity]);

  return (
    <View style={styles.telepromter}>
      <Animated.View style={{ transform: [{ translateY }], opacity }}>
        <ThemedText style={styles.message}>{messages[idx]}</ThemedText>
      </Animated.View>
    </View>
  );
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const { t } = useTranslation();

  const removeToast = (id: number) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const addToast = ((messageOrMessages: string | string[], type: ToastType) => {
    const id = Date.now();
    const messages = Array.isArray(messageOrMessages)
      ? messageOrMessages
      : [messageOrMessages];
    const newToast: ToastMessage = { id, messages, type };

    setToasts([newToast]);

    if (type === "success" || type === "error") {
      setTimeout(() => {
        removeToast(id);
      }, 3000);
    } else if (type === "info") {
      setTimeout(() => {
        removeToast(id);
      }, 5000);
    }

    return id;
  }) as AddToast;

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ThemedView style={styles.container} pointerEvents="box-none">
        {toasts.map((toast) => (
          <ThemedView
            key={toast.id}
            style={[
              styles.toast,
              {
                backgroundColor:
                  toast.type === "success"
                    ? "#CDFFCD"
                    : toast.type === "error"
                    ? "#F08080"
                    : "white",
                borderColor:
                  toast.type === "success"
                    ? "#6FCF6F" // darker green
                    : toast.type === "error"
                    ? "#C0392B" // darker red
                    : "#888", // darker gray
                borderWidth: 1,
              },
            ]}
          >
            {toast.type === "loading" ? (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color="#444" />
                <TelepromterText messages={toast.messages} />
              </View>
            ) : (
              <View style={styles.dismissRow}>
                <ThemedText style={[styles.message, styles.dismissMessage]}>
                  {toast.messages[0]}
                </ThemedText>
                <Pressable
                  onPress={() => removeToast(toast.id)}
                  accessibilityLabel={t("CLOSE")}
                  accessibilityRole="button"
                  hitSlop={8}
                  style={styles.dismissButton}
                >
                  <IconSymbol
                    name="xmark.circle.fill"
                    color="#444"
                    size={18}
                  />
                </Pressable>
              </View>
            )}
          </ThemedView>
        ))}
      </ThemedView>
    </ToastContext.Provider>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 35,
    left: 0,
    right: 0,
    alignItems: "center",
    zIndex: 9999,
  },
  toast: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 5,
    opacity: 0.9,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  message: {
    color: "black",
    lineHeight: LINE_HEIGHT,
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  dismissRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  dismissMessage: {
    flexShrink: 1,
  },
  dismissButton: {
    marginLeft: "auto",
  },
  telepromter: {
    height: LINE_HEIGHT,
    overflow: "hidden",
    justifyContent: "center",
  },
});
